import Picture from 'components/Picture';
import {formatPrice, plural} from 'core/helpers';
import {getMediaSrc} from 'core/helpers/media';
import {ProductCardActions} from 'modules/product-card-actions/components/ProductCardActions';
import {IProductShort} from 'modules/products/types';
import React, {FC, useMemo} from 'react';
import {Shield} from 'components/Shield';
import {Typography} from 'components/Typography';
import NextLink from 'components/CustomRoutingLink';
import Link from 'components/Link';

import style from './style.module.scss';
import {useIsPrive} from 'core/hooks/useIsPrive';
import {withRedirectParam} from 'modules/product-card/helpers/redirect';
import cs from 'classnames';
import {IAnalyticsMeta} from 'core/types';
import Icon from 'components/Icon';
import {useReservationFlow} from 'modules/product-snippet/components/ProductSnippetContainer/hooks/useReservationFlow';
import {useIsWorker} from 'core/hooks/useIsWorker';
import {useIsLoggedIn} from 'modules/user/hooks/useIsLoggedIn';

interface IProps {
    product: IProductShort & {article?: number};
    analytics?: IAnalyticsMeta;
    onProductClick?: () => void;
}

// eslint-disable-next-line complexity
export const SearchProductCard: FC<IProps> = ({product, analytics, onProductClick}) => {
    const {
        price,
        media: {imagePreview},
        id,
        article,
        title,
        ratings,
        url,
        canBuy,
        canReserve,
        description,
    } = product;

    const isPrive = useIsPrive();
    const isWorker = useIsWorker();
    const isLoggedIn = useIsLoggedIn();

    const {isReservationFlowActive, storesWithStockCount} = useReservationFlow({
        productData: product,
        isPrive,
        isWorker,
        isLoggedIn,
        availability: 'FOR_RESERVATION',
    });

    const winestoresText = plural(storesWithStockCount, ['винотеке', 'винотеках', 'винотеках']);

    const {discountValue, price: discountPrice} = price.discount;
    const isDiscountPrice = discountPrice !== price.base.price;

    const outOfStock = isPrive ? !canBuy : !canBuy && !canReserve;
    const showsReservationFlowInfo = isReservationFlowActive && 0 < storesWithStockCount;
    // InStoreButton показывает свой лейбл, только если И сам рендерится (canReserve/canBuy/isPrive),
    // И у него есть что показать — те же isReservationFlowActive/storesWithStockCount, что и здесь,
    // т.к. useReservationFlow с availability='FOR_RESERVATION' не зависит от конкретного товара
    const showsInStoreButtonFrame = canReserve && !isPrive && !canBuy && showsReservationFlowInfo;

    const renderActions = useMemo<React.ReactNode>(
        () => (
            <div className={style.productActionsWrapper}>
                {
                    <ProductCardActions
                        className={style.productActions}
                        isGiftCard={true}
                        variant={'searchRecommend'}
                        analytics={analytics}
                        {...(product as any)}
                    />
                }
            </div>
        ),
        [product, analytics],
    );

    const reviewsValue = product.ratings?.find((rating) => 'REVIEWS' === rating.code)?.value ?? 0;

    return (
        <div className={style.productCardRecommend}>
            <div className={style.productCardWrapper}>
                {imagePreview && (
                    <NextLink href={withRedirectParam(url)} legacyBehavior={true} passHref={true}>
                        <a className={style.imageContainer} onClick={onProductClick}>
                            <Picture alt={title || ''} sources={{}} src={getMediaSrc(imagePreview)} />
                        </a>
                    </NextLink>
                )}
                <div>
                    <div className={style.rowContainer}>
                        {0 < reviewsValue && (
                            <Shield
                                backColor={'gray'}
                                className={style.shield}
                                customFontVariant={'label-10-m-up'}
                                isRating={true}
                                size={'small'}
                            >
                                {ratings[0].value.toString()}
                            </Shield>
                        )}
                        <Typography
                            className={style.articleContainer}
                            color={'gray60'}
                            element={'div'}
                            variant={'label-14-r'}
                        >
                            <span className={style.article}>Артикул:</span> {id ?? article}
                        </Typography>
                    </div>
                    <Link href={withRedirectParam(url)} legacyBehavior={true} theme={'black'} onClick={onProductClick}>
                        <Typography
                            className={cs(style.title, style.text)}
                            element={'div'}
                            variant={'snippet-title-16-r'}
                        >
                            {title}
                        </Typography>
                    </Link>
                    <Typography
                        className={style.text}
                        color={'gray60'}
                        element={'div'}
                        variants={{
                            DT: 'label-14-r',
                            TB: 'label-14-r',
                            MB: 'label-12-r',
                        }}
                    >
                        {description}
                    </Typography>
                    {outOfStock && (
                        <Typography
                            className={style.text}
                            color={'black100'}
                            element={'div'}
                            variants={{
                                DT: 'label-14-r',
                                TB: 'label-14-r',
                                MB: 'label-12-r',
                            }}
                        >
                            Этот товар закончился
                        </Typography>
                    )}
                </div>
            </div>
            <div className={style.priceContainer}>
                <div className={style.oldPriceContainer}>
                    {isDiscountPrice && !outOfStock && (
                        <>
                            <Typography className={style.oldPrice} color={'primary60'} variant={'label-12-r-lt'}>
                                {price.base.price}&nbsp;₽
                            </Typography>
                            <Typography color={'primary100'} variant={'label-12-r'}>
                                -{discountValue}%
                            </Typography>
                        </>
                    )}
                    <Typography
                        className={style.price}
                        element={'div'}
                        variants={{DT: 'price-18-b', TB: 'body-18-m', MB: 'price-20-b'}}
                    >
                        {isDiscountPrice && !outOfStock
                            ? formatPrice(String(discountPrice))
                            : formatPrice(String(price.base.price))}{' '}
                        ₽
                    </Typography>
                </div>
                {!showsInStoreButtonFrame && <div className={style.actionsFramePlaceholder} aria-hidden={true} />}
                {renderActions}
            </div>
            {showsReservationFlowInfo ? (
                <div className={style.reservationFlowSearchContainer}>
                    <Icon icon={'Express'} className={style.expressIcon} />
                    <Typography variant={'label-12-r'}>
                        Только в {storesWithStockCount} {winestoresText}
                    </Typography>
                </div>
            ) : (
                <div className={style.reservationFlowSearchContainerPlaceholder} aria-hidden={true} />
            )}
        </div>
    );
};
